import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../components/AdminSidebar';
import LanguageToggle from '../components/LanguageToggle';
import { Save, RotateCcw, LogOut } from 'lucide-react';
import { apiClient } from '../lib/apiClient';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT = {
  available_color: '#22c55e',
  reserved_color: '#f59e0b',
  unavailable_color: '#ef4444',
  transparency: 70,
};

function hexToRgba(hex, alpha) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha / 100})`;
}

const ColorRow = ({ label, desc, colorKey, settings, onChange }) => (
  <div className="flex items-center gap-4 p-4 rounded-2xl border border-gray-100 bg-white hover:border-purple-100 transition-colors">
    <div
      className="w-10 h-10 rounded-xl shadow-sm flex-shrink-0"
      style={{ background: hexToRgba(settings[colorKey], settings.transparency) }}
    />
    <div className="flex-1 min-w-0">
      <p className="font-semibold text-gray-800 text-sm">{label}</p>
      <p className="text-xs text-gray-400">{desc}</p>
    </div>
    <div className="flex items-center gap-3">
      <label className="text-xs text-gray-500 font-medium">Renk</label>
      <input
        type="color"
        value={settings[colorKey]}
        onChange={e => onChange(colorKey, e.target.value)}
        className="w-10 h-10 rounded-xl cursor-pointer border border-gray-200 p-0.5"
        title="Renk seç"
      />
      <span className="text-xs text-gray-400 font-mono w-16">{settings[colorKey]}</span>
    </div>
  </div>
);

const GridSettingsPage = () => {
  const navigate = useNavigate();
  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('cop31_ultra_token')}` });

  const [settings, setSettings] = useState(DEFAULT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await apiClient.get(`/ultra-admin/grid-settings`);
        setSettings({ ...DEFAULT, ...res.data });
      } catch {}
      setLoading(false);
    };
    fetch();
    const handleVisible = () => { if (document.visibilityState === 'visible') fetch(); };
    document.addEventListener('visibilitychange', handleVisible);
    return () => document.removeEventListener('visibilitychange', handleVisible);
  }, []);

  const handleChange = (key, value) => {
    setSettings(s => ({ ...s, [key]: value }));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.put(`/ultra-admin/grid-settings`, settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const handleReset = () => {
    setSettings(DEFAULT);
    setSaved(false);
  };

  // Preview stands
  const previewStands = [
    { label: 'Müsait Stand', status: 'available', stand_no: 'A-101' },
    { label: 'Rezerve Stand', status: 'reserved', stand_no: 'B-205' },
    { label: 'Müsait Değil', status: 'unavailable', stand_no: 'C-310' },
    { label: 'Atanmış Stand', status: 'assigned', stand_no: 'TR-001', tenant: 'Türkiye' },
  ];

  const getPreviewColor = (stand) => {
    if (stand.status === 'assigned') return hexToRgba('#3b82f6', settings.transparency);
    if (stand.status === 'reserved') return hexToRgba(settings.reserved_color, settings.transparency);
    if (stand.status === 'unavailable') return hexToRgba(settings.unavailable_color, settings.transparency);
    return hexToRgba(settings.available_color, settings.transparency);
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <AdminSidebar />
      <div className="flex-1 flex flex-col">
        <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-800">Grid Plan Ayarları</h1>
            <p className="text-xs text-gray-500 mt-0.5">Hall editöründe stand renklerini ve görünümünü ayarlayın</p>
          </div>
          <div className="flex items-center gap-3">
            <LanguageToggle />
            <button onClick={() => { localStorage.clear(); navigate('/giris'); }}
              className="text-gray-400 hover:text-red-500 transition-colors">
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 p-6 max-w-4xl">
          {loading ? (
            <div className="text-center text-gray-400 py-10">Yükleniyor...</div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left: Settings */}
              <div className="space-y-4">
                <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
                  <h2 className="font-bold text-gray-800 mb-4 text-sm">Stand Durum Renkleri</h2>
                  <div className="space-y-3">
                    <ColorRow
                      label="Müsait" desc="Atanmamış, rezervasyon yapılabilir"
                      colorKey="available_color" settings={settings} onChange={handleChange}
                    />
                    <ColorRow
                      label="Rezerve" desc="Beklemede, ön rezervasyon yapılmış"
                      colorKey="reserved_color" settings={settings} onChange={handleChange}
                    />
                    <ColorRow
                      label="Müsait Değil" desc="Satışa kapalı, kullanım dışı"
                      colorKey="unavailable_color" settings={settings} onChange={handleChange}
                    />
                  </div>
                </div>

                {/* Transparency */}
                <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="font-bold text-gray-800 text-sm">Renk Saydamlığı</h2>
                    <span className="text-lg font-bold text-purple-700">%{settings.transparency}</span>
                  </div>
                  <input
                    type="range" min="10" max="100" step="5"
                    value={settings.transparency}
                    onChange={e => handleChange('transparency', parseInt(e.target.value))}
                    className="w-full accent-purple-600"
                  />
                  <div className="flex justify-between text-xs text-gray-400 mt-1">
                    <span>%10 (şeffaf)</span>
                    <span>%100 (tam dolu)</span>
                  </div>
                  {/* Transparency preview row */}
                  <div className="flex gap-2 mt-3">
                    {[10, 30, 50, 70, 90, 100].map(v => (
                      <button key={v}
                        onClick={() => handleChange('transparency', v)}
                        className={`flex-1 h-8 rounded-lg text-xs font-bold transition-all border-2 ${settings.transparency === v ? 'border-purple-500' : 'border-transparent'}`}
                        style={{ background: hexToRgba(settings.available_color, v) }}>
                        {v}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex gap-3">
                  <button onClick={handleReset}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors">
                    <RotateCcw className="w-4 h-4" /> Sıfırla
                  </button>
                  <button onClick={handleSave} disabled={saving}
                    data-testid="save-grid-settings"
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-white text-sm font-semibold disabled:opacity-50 transition-all shadow-md hover:shadow-lg"
                    style={{ background: saved ? 'linear-gradient(135deg, #10b981, #059669)' : 'linear-gradient(135deg, #7c3aed, #4f46e5)' }}>
                    <Save className="w-4 h-4" />
                    {saving ? 'Kaydediliyor...' : saved ? 'Kaydedildi!' : 'Kaydet'}
                  </button>
                </div>
              </div>

              {/* Right: Preview */}
              <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
                <h2 className="font-bold text-gray-800 mb-4 text-sm">Önizleme</h2>
                <p className="text-xs text-gray-400 mb-4">Hall editöründe standların görünümü</p>

                {/* Mini hall preview */}
                <div className="bg-gray-50 rounded-xl p-3 mb-4">
                  <svg width="100%" viewBox="0 0 280 200" style={{ display: 'block' }}>
                    {/* Hall border */}
                    <rect x="10" y="10" width="260" height="180" fill="#f8fafc" stroke="#e2e8f0" strokeWidth="1" rx="2" />

                    {/* Grid lines */}
                    {Array.from({ length: 13 }, (_, i) => (
                      <line key={`v${i}`} x1={10 + i * 20} y1="10" x2={10 + i * 20} y2="190" stroke="#e2e8f0" strokeWidth="0.5" />
                    ))}
                    {Array.from({ length: 10 }, (_, i) => (
                      <line key={`h${i}`} x1="10" y1={10 + i * 20} x2="270" y2={10 + i * 20} stroke="#e2e8f0" strokeWidth="0.5" />
                    ))}

                    {/* Preview stands */}
                    <rect x="20" y="20" width="60" height="60" fill={hexToRgba(settings.available_color, settings.transparency)} stroke={settings.available_color} strokeWidth="0.8" rx="1" />
                    <text x="50" y="52" textAnchor="middle" fontSize="8" fontWeight="bold" fill={settings.available_color}>A-101</text>

                    <rect x="100" y="20" width="60" height="40" fill={hexToRgba(settings.reserved_color, settings.transparency)} stroke={settings.reserved_color} strokeWidth="0.8" rx="1" />
                    <text x="130" y="42" textAnchor="middle" fontSize="8" fontWeight="bold" fill={settings.reserved_color}>B-205</text>

                    <rect x="180" y="20" width="40" height="80" fill={hexToRgba(settings.unavailable_color, settings.transparency)} stroke={settings.unavailable_color} strokeWidth="0.8" rx="1" />
                    <text x="200" y="62" textAnchor="middle" fontSize="8" fontWeight="bold" fill={settings.unavailable_color}>C-310</text>

                    <rect x="20" y="100" width="80" height="60" fill={hexToRgba('#3b82f6', settings.transparency)} stroke="#3b82f6" strokeWidth="0.8" rx="1" />
                    <text x="60" y="127" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#3b82f6">TR-001</text>
                    <text x="60" y="138" textAnchor="middle" fontSize="6" fill="#64748b">Türkiye</text>
                  </svg>
                </div>

                {/* Legend */}
                <div className="space-y-2">
                  {previewStands.map(s => (
                    <div key={s.status} className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-lg flex-shrink-0 border"
                        style={{
                          background: getPreviewColor(s),
                          borderColor: s.status === 'assigned' ? '#3b82f6' : settings[`${s.status}_color`] || '#94a3b8'
                        }} />
                      <div>
                        <span className="text-xs font-semibold text-gray-700">{s.label}</span>
                        {s.tenant && <span className="text-xs text-gray-400 ml-1">— {s.tenant}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GridSettingsPage;
